/**
 * WCAG 2.x contrast utilities.
 *
 * Used by the questionnaire's colour step to surface a real-time contrast
 * ratio between the user's primary (text/ink) and secondary (background)
 * colours, plus a "use compliant version" nudge that walks the offending
 * colour towards the threshold by adjusting HSL lightness one step at a
 * time. The 4.5:1 default is the WCAG AA threshold for normal-size body
 * text.
 *
 * No external dependencies; all maths in pure TypeScript so this can run
 * during a re-render without lag.
 */

// ─────────────────────────────────────────────────────────────────────────
//  Hex parsing
// ─────────────────────────────────────────────────────────────────────────

/**
 * Parse a #RGB or #RRGGBB hex string into [r, g, b] in 0..255.
 * Returns null on malformed input (caller should treat as "skip the
 * contrast check entirely" rather than guess a fallback).
 */
export function parseHex(hex: string): [number, number, number] | null {
  if (typeof hex !== 'string') return null;
  const clean = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]+$/.test(clean)) return null;
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return [r, g, b];
  }
  if (clean.length === 6) {
    return [
      parseInt(clean.slice(0, 2), 16),
      parseInt(clean.slice(2, 4), 16),
      parseInt(clean.slice(4, 6), 16),
    ];
  }
  return null;
}

function toHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}

// ─────────────────────────────────────────────────────────────────────────
//  RGB ↔ HSL
// ─────────────────────────────────────────────────────────────────────────

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(
  h: number,
  s: number,
  l: number,
): [number, number, number] {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hp >= 0 && hp < 1) [rp, gp, bp] = [c, x, 0];
  else if (hp < 2) [rp, gp, bp] = [x, c, 0];
  else if (hp < 3) [rp, gp, bp] = [0, c, x];
  else if (hp < 4) [rp, gp, bp] = [0, x, c];
  else if (hp < 5) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  const m = ln - c / 2;
  return [(rp + m) * 255, (gp + m) * 255, (bp + m) * 255];
}

// ─────────────────────────────────────────────────────────────────────────
//  Luminance & contrast
// ─────────────────────────────────────────────────────────────────────────

function srgbToLinear(channel0to255: number): number {
  const c = channel0to255 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Relative luminance per WCAG 2.x. Returns 0..1. */
export function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
}

/**
 * WCAG contrast ratio between two colours. Range 1..21.
 * Returns null if either hex is unparseable.
 */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

// ─────────────────────────────────────────────────────────────────────────
//  Compliance nudge
// ─────────────────────────────────────────────────────────────────────────

export interface NudgeResult {
  adjusted: string; // new hex for the nudged colour
  ratioBefore: number;
  ratioAfter: number;
  /** Which colour we ended up moving to reach the target. */
  movedKey: 'foreground' | 'background';
}

/**
 * Walk one of two colours towards compliance with `target` contrast against
 * the other, by adjusting HSL lightness in 1% steps. Returns the smaller
 * of the two valid moves (the one that disturbs the palette least).
 *
 * The strategy:
 *   1. Try moving the background. Lighten if foreground is darker than
 *      background; darken otherwise.
 *   2. If that hits 0 / 100 lightness without reaching target, try the
 *      foreground in the opposite direction.
 *   3. Pick whichever moved fewer steps.
 *
 * Returns null if neither colour can reach the target in 100 steps (this
 * really only happens with two colours of identical luminance in a
 * narrow hue band — unlikely in practice).
 */
export function nudgeForContrast(
  foreground: string,
  background: string,
  target = 4.5,
): NudgeResult | null {
  const before = contrastRatio(foreground, background);
  if (before === null) return null;
  if (before >= target) {
    return {
      adjusted: background,
      ratioBefore: before,
      ratioAfter: before,
      movedKey: 'background',
    };
  }

  const fgLum = relativeLuminance(foreground);
  const bgLum = relativeLuminance(background);
  if (fgLum === null || bgLum === null) return null;

  const tryMove = (
    hex: string,
    direction: 'lighter' | 'darker',
  ): { hex: string; steps: number } | null => {
    const rgb = parseHex(hex);
    if (!rgb) return null;
    const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
    const sign = direction === 'lighter' ? +1 : -1;
    for (let step = 1; step <= 100; step++) {
      const newL = Math.max(0, Math.min(100, l + sign * step));
      const [r, g, b] = hslToRgb(h, s, newL);
      const candidate = toHex(r, g, b);
      const ratio = contrastRatio(foreground === hex ? candidate : foreground,
                                  background === hex ? candidate : background);
      if (ratio !== null && ratio >= target) {
        return { hex: candidate, steps: step };
      }
      if (newL === 0 || newL === 100) break;
    }
    return null;
  };

  // Background moves AWAY from foreground luminance.
  const bgDir: 'lighter' | 'darker' = fgLum < bgLum ? 'lighter' : 'darker';
  const fgDir: 'lighter' | 'darker' = fgLum < bgLum ? 'darker' : 'lighter';

  const bgMove = tryMove(background, bgDir);
  const fgMove = tryMove(foreground, fgDir);

  if (!bgMove && !fgMove) return null;

  // Pick the smaller move (perturb the palette less).
  const useBg =
    bgMove && (!fgMove || bgMove.steps <= fgMove.steps);

  if (useBg && bgMove) {
    const ratioAfter = contrastRatio(foreground, bgMove.hex) ?? before;
    return {
      adjusted: bgMove.hex,
      ratioBefore: before,
      ratioAfter,
      movedKey: 'background',
    };
  }
  if (fgMove) {
    const ratioAfter = contrastRatio(fgMove.hex, background) ?? before;
    return {
      adjusted: fgMove.hex,
      ratioBefore: before,
      ratioAfter,
      movedKey: 'foreground',
    };
  }
  return null;
}

/** WCAG AA threshold for normal-size body text. */
export const WCAG_AA_NORMAL = 4.5;
